import {
  type ReactNode,
  type LazyExoticComponent,
  type ComponentType,
} from "react";

import { createRoot, type Root as ReactRoot } from "react-dom/client";
import { type IntersectionOptions } from "react-intersection-observer";

import { camelize } from "@react/utilities/general";
import { zones as defaultZones } from "@react/zones";
import { Root } from "@sections/Root";

const mountedRoots = new Map<HTMLElement, ReactRoot>();

type RootProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: LazyExoticComponent<ComponentType<any>>;
  observerSettings: IntersectionOptions;
  placeholder: ReactNode | null;
  sectionId: string;
};

export function renderRootZones(zones: typeof defaultZones = defaultZones) {
  // Section swaps detach containers without unmounting React. Retire those
  // trees before mounting replacements so old modals stop receiving events.
  mountedRoots.forEach((root, container) => {
    if (!container.isConnected) {
      root.unmount();
      mountedRoots.delete(container);
    }
  });

  zones.forEach((zone) => {
    const rootNodes = document.querySelectorAll<HTMLElement>(zone.query);

    rootNodes.forEach((rootNode) => {
      if (mountedRoots.has(rootNode)) {
        return;
      }

      const componentName = rootNode.dataset.tbspComponent ?? "";

      if (!componentName.length && zone.hydrate) {
        console.warn("No component name found for zone", zone.name);
        return;
      }

      let rootProps: RootProps = {
        children: zone.component(componentName),
        observerSettings: {
          initialInView: elementIsVisibleInViewport(rootNode),
          ...zone.observerSettings,
        },
        placeholder: null,
        sectionId: "",
      };

      let hydratedProps = {};

      if (zone.hydrate) {
        try {
          const dataEl = rootNode.querySelector<HTMLScriptElement>(
            zone.settingsQuery
          );
          const data = camelize<Record<string, unknown>>(
            JSON.parse(dataEl?.innerHTML || "{}")
          );
          const sectionId = dataEl?.dataset.sectionId ?? "";

          hydratedProps = {
            sectionId,
            ...data,
          };
        } catch (e) {
          console.error(e);
        }
      }

      rootProps = {
        ...rootProps,
        ...hydratedProps,
        placeholder: zone.placeholder?.(componentName, hydratedProps) ?? null,
      };

      const root = createRoot(rootNode);
      mountedRoots.set(rootNode, root);

      root.render(<Root {...rootProps} />);
    });
  });
}

export function filterZonesBySectionId(sectionId?: string) {
  return defaultZones.map((zone) => ({
    ...zone,
    query: sectionId
      ? `#shopify-section-${sectionId} ${zone.query}`
      : `[id^="shopify-section-"] ${zone.query}`,
  }));
}

export function elementIsVisibleInViewport(
  el: HTMLElement,
  partiallyVisible = false
) {
  const { bottom, left, right, top } = el.getBoundingClientRect();
  const { innerHeight, innerWidth } = window;
  return partiallyVisible
    ? ((top > 0 && top < innerHeight) ||
        (bottom > 0 && bottom < innerHeight)) &&
        ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
    : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
}
